import React, { createContext, useState, useContext, useCallback } from 'react';
import { CreateNotificationOverlay, ConfirmModal } from '../components/Notification';

const NotificationContext = createContext(null);

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [confirmDialog, setConfirmDialog] = useState(null);

  const confirm = useCallback((options) => {
    return new Promise((resolve) => {
      setConfirmDialog({
        ...options,
        resolve
      });
    });
  }, []);

  const handleConfirmClose = useCallback((result) => {
    if (confirmDialog && confirmDialog.resolve) {
      confirmDialog.resolve(result);
    }
    setConfirmDialog(null);
  }, [confirmDialog]);

  const showNotification = useCallback((notification) => {
    const id = Date.now();
    const newNotification = {
      id,
      type: 'info', // 'success', 'error', 'info', 'warning'
      duration: 3000,
      ...notification
    };

    setNotifications((prev) => [...prev, newNotification]);

    if (newNotification.duration !== Infinity) {
      setTimeout(() => {
        setNotifications((prev) => prev.filter((n) => n.id !== id));
      }, newNotification.duration);
    }

    return id;
  }, []);

  const hideNotification = useCallback((id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  // Quick helper methods
  const success = useCallback((message, options = {}) => 
    showNotification({ ...options, message, type: 'success' }), [showNotification]);
  
  const error = useCallback((message, options = {}) => 
    showNotification({ ...options, message, type: 'error' }), [showNotification]);
  
  const info = useCallback((message, options = {}) => 
    showNotification({ ...options, message, type: 'info' }), [showNotification]);
  
  const warning = useCallback((message, options = {}) => 
    showNotification({ ...options, message, type: 'warning' }), [showNotification]);

  const value = {
    notifications,
    confirmDialog,
    showNotification,
    hideNotification,
    success,
    error,
    info,
    warning,
    confirm
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <CreateNotificationOverlay notifications={notifications} onClose={hideNotification} />
      {confirmDialog && (
        <ConfirmModal 
          {...confirmDialog} 
          onClose={() => handleConfirmClose(false)} 
          onConfirm={() => handleConfirmClose(true)} 
        />
      )}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

export default NotificationContext;
